import { useEffect, useState } from "react";
import { ArrowUpRight, ExternalLink } from "lucide-react";
import axios from "axios";
import { IMAGES } from "../../lib/ablemp-data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_COLORS = {
  Notícia: "#002776",
  Artigo: "#009B3A",
  Manifesto: "#0A0F14",
  Posicionamento: "#475569",
};

const formatDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "";
  }
};

export const News = () => {
  const [items, setItems] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/public/news`)
      .then((r) => setItems((r.data || []).slice(0, 3)))
      .catch(() => setItems([]));
  }, []);

  return (
    <section
      id="noticias"
      data-testid="news-section"
      className="relative bg-[#F4F4F0] bg-grain text-[#0B1015] py-24 md:py-36"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="flex items-center gap-4 text-[11px] uppercase tracking-[0.3em] text-[#475569]">
          <span className="flag-rule" aria-hidden />
          <span>§ 04 — Notícias e Artigos</span>
        </div>

        <div className="mt-10 grid grid-cols-12 gap-8 items-end">
          <h2
            data-testid="news-headline"
            className="col-span-12 lg:col-span-9 font-serif-display text-[36px] sm:text-[48px] lg:text-[72px] leading-[1.02] tracking-[-0.02em]"
          >
            A voz da ABLEMP no{" "}
            <span className="italic font-light text-[#002776]">debate</span>{" "}
            público.
          </h2>
          <div className="col-span-12 lg:col-span-3 text-right">
            <a
              data-testid="news-see-all"
              href="/noticias-e-artigos"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-[#0B1015] editorial-link hover:active"
            >
              Ver todas <ExternalLink size={13} />
            </a>
          </div>
        </div>

        {items === null ? (
          <div className="mt-16 text-sm text-[#475569]">
            Carregando publicações...
          </div>
        ) : items.length === 0 ? (
          <div className="mt-16 border border-dashed border-[#0B1015]/20 bg-white p-12 text-center">
            <div className="font-serif-display text-2xl text-[#0B1015]">
              Em breve, as primeiras publicações
            </div>
            <p className="mt-2 text-sm text-[#475569]">
              A administração da ABLEMP está preparando os primeiros
              posicionamentos e análises. Volte em breve.
            </p>
          </div>
        ) : (
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-px bg-[#0B1015]/12 border border-[#0B1015]/12">
            {items.map((n, i) => (
              <a
                key={n.id}
                href={`/noticias-e-artigos/${n.slug}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`news-card-${i}`}
                className="bg-[#F4F4F0] hover:bg-white transition-colors duration-500 p-8 md:p-10 group cursor-pointer block"
              >
                <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.3em] text-[#475569]">
                  <span
                    className="px-2 py-1 border"
                    style={{
                      borderColor:
                        (CATEGORY_COLORS[n.category] || "#0B1015") + "40",
                      color: CATEGORY_COLORS[n.category] || "#0B1015",
                    }}
                  >
                    {n.category}
                  </span>
                  <span>{formatDate(n.published_at)}</span>
                </div>

                <h3 className="mt-8 font-serif-display text-2xl md:text-[26px] leading-tight line-clamp-3">
                  {n.title}
                </h3>

                {n.summary && (
                  <p className="mt-4 text-[15px] text-[#475569] leading-relaxed line-clamp-3">
                    {n.summary}
                  </p>
                )}

                <div className="mt-8 flex items-center justify-between">
                  <span className="text-[11px] uppercase tracking-[0.3em] text-[#0B1015] inline-flex items-center gap-2 editorial-link group-hover:active">
                    Ler matéria
                  </span>
                  <ArrowUpRight
                    size={20}
                    className="text-[#0B1015] group-hover:text-[#002776] group-hover:-translate-y-1 group-hover:translate-x-1 transition-transform duration-300"
                  />
                </div>
              </a>
            ))}
          </div>
        )}

        {/* Mix payment methods banner */}
        <div
          data-testid="news-banner"
          className="mt-16 grid grid-cols-1 md:grid-cols-12 gap-0 border border-[#0B1015]/15 overflow-hidden"
        >
          <div className="md:col-span-7 relative aspect-[16/9] md:aspect-auto md:min-h-[360px] overflow-hidden">
            <img
              src={IMAGES.realNotesBlue}
              alt="Cédulas de Real brasileiro em destaque"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0A0F14]/85 via-[#0A0F14]/40 to-transparent" />
            <div className="absolute inset-0 flex items-center px-8 md:px-12">
              <div className="max-w-xl text-white">
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#FEDF00]">
                  Em pauta
                </div>
                <h3 className="mt-3 font-serif-display text-2xl md:text-4xl leading-tight">
                  Toda recusa de meio de pagamento é uma decisão política. Vamos
                  conversar sobre isso.
                </h3>
              </div>
            </div>
          </div>
          <div className="md:col-span-5 grid grid-cols-2 md:grid-cols-1">
            <div className="relative aspect-square md:aspect-auto md:min-h-[180px] overflow-hidden border-l border-[#0B1015]/15 md:border-b">
              <img
                src={IMAGES.cardPayment}
                alt="Pagamento com cartão por aproximação"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-[#0A0F14]/40" />
              <span className="absolute top-3 left-3 text-[10px] uppercase tracking-[0.3em] text-[#FEDF00]">
                Cartão
              </span>
            </div>
            <div className="relative aspect-square md:aspect-auto md:min-h-[180px] overflow-hidden border-l border-[#0B1015]/15">
              <img
                src={IMAGES.pixQr}
                alt="Pagamento Pix por QR code"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-[#0A0F14]/40" />
              <span className="absolute top-3 left-3 text-[10px] uppercase tracking-[0.3em] text-[#FEDF00]">
                Pix
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default News;
